'use client'

import { useEffect, useState } from 'react'
import { Icon } from '@/icons/Icon'
import { AnimatePresence, motion } from 'motion/react'
import Link from '@/components/ui/Link'
import Image from 'next/image'
import { cn } from '@/lib/utils'

type FTCProps = {
  position?: 'badge' | 'inline'
}

export default function FTC({ position = 'badge' }: FTCProps) {
  const [showModal, setShowModal] = useState(false)
  const [mobile, setMobile] = useState(false)

  const positions = {
    badge: 'absolute bottom-3 left-3 z-30',
    inline: 'relative group',
  }

  const xValue = position === 'inline' ? (mobile ? '0' : '-50%') : 0

  useEffect(() => {
    const handleResize = () => {
      setMobile(window.innerWidth < 640)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const label = (
    <div className={`flex items-center gap-2 text-13 text-zinc-400 md:gap-1`}>
      <span>FTC</span> <span className="hidden md:inline-block">compliant</span>
      <div className="md:hidden">
        <Icon
          name="chevron-left"
          className={cn('-rotate-90 transition-transform', showModal && 'rotate-90')}
          size={10}
        />
      </div>
    </div>
  )

  return (
    <div
      data-review="ignore"
      className={cn(positions[position], 'meticulous-ignore')}
      onMouseEnter={() => setShowModal(true)}
      onMouseLeave={() => setShowModal(false)}
      onClick={() => setShowModal(!showModal)}
    >
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0, y: 10, x: xValue }}
            animate={{ opacity: 1, y: 0, x: xValue }}
            exit={{ opacity: 0, y: 10, x: xValue }}
            className={
              position === 'inline'
                ? 'absolute right-0 top-full pt-1 sm:left-1/2 sm:right-auto'
                : 'absolute bottom-full pb-1'
            }
          >
            <div className="flex w-[330px] items-center gap-5 rounded-xl border border-white/20 bg-black/70 px-4 py-2.5 backdrop-blur-md">
              <div className="flex flex-col border-r border-white/20 pr-5 text-13">
                <p className="w-[185px] text-zinc-400">
                  Independent reviews. We may earn a commission from links.
                </p>
                <Link
                  href="/about"
                  className="flex w-fit items-center gap-1.5 text-primary-500 transition-colors hover:text-white"
                >
                  Learn how we review <Icon name="chevron-left" className="rotate-180" size={10} />
                </Link>
              </div>
              <Image src="/img/global/ftc.svg" alt="FTC" width={72} height={72} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {position === 'inline' ? (
        <div className="inline-flex h-6 cursor-default items-center gap-2 rounded-full px-2.5 text-13 text-zinc-400 backdrop-blur-md transition-colors hover:bg-white/10 hover:text-white">
          <Icon name="shield" className="text-success-500" size={12} />
          {label}
        </div>
      ) : (
        <div className="inline-flex cursor-default rounded-lg bg-gradient-to-b from-white/20 to-white/0 p-px backdrop-blur-md">
          <div className="flex h-8 items-center gap-2 rounded-lg bg-black/60 px-2.5">
            <Icon name="shield" className="text-success-500" size={12} />
            {label}
          </div>
        </div>
      )}
    </div>
  )
}
